import {
  Text,
  Button,
  Stack,
  Flex,
  Box,
  Avatar,
  Slider,
  Textarea,
} from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useProfileStore from "../../store/useProfileStore";
import AvatarEditor from "react-avatar-editor";
import useAuthStore from "../../store/useAuthStore";
import { uploadAvatar } from "../../api/userApi";
import isEqual from "lodash/isEqual";

import {
  EmailIcon,
  FacebookIcon,
  LinkedInIcon,
  LocationIcon,
  PhoneIcon,
  TwitterIcon,
} from "../../shared/svg";
import { GithubIcon } from "../../shared/svg/GithubIcon";
import { CityIcon } from "../../shared/svg/CityIcon";
import { UserIcon } from "../../shared/svg/UserIcon";
import {
  fetchPersonalInfo,
  updatePersonalInfo,
} from "../../api/profileEditingApi";
import { DeepPartial, PersonalData } from "../../types/profile-editing";

import { notifications } from "@mantine/notifications";

import { EditIconSmall } from "../../shared/svg/EditIconSmall";
import { useMediaQuery } from "@mantine/hooks";
import { IconDescription } from "../../shared/svg/IconDescription";

const iconMap: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  email: EmailIcon,
  phone: PhoneIcon,
  location: LocationIcon,
  city: CityIcon,
  linkedIn: LinkedInIcon,
  twitter: TwitterIcon,
  facebook: FacebookIcon,
  github: GithubIcon,
  name: UserIcon,
  self_description: IconDescription,
};
const renderField = (
  label: string,
  value: string,
  onChange: (v: string) => void,
  key?: string,
  error?: string[]
) => {
  const Icon = key ? iconMap[key] : null;
  const isTextarea = key === "self_description";

  return (
    <>
      <Box className="edit-input-box">
        <Flex align="center" gap={12} w="100%">
          {Icon && (
            <Box
              style={{
                flexShrink: 0,
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                backgroundColor: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
              }}
            >
              <Icon />
            </Box>
          )}
          <Box w="100%">
            <Text className="edit-label">{label}</Text>
            {isTextarea ? (
              <Textarea
                value={value}
                onChange={(e) => onChange(e.currentTarget.value)}
                autosize
                p={0}
                minRows={1}
                variant="unstyled"
                style={{
                  border: "none",
                  outline: "none",
                  backgroundColor: "transparent",
                  fontSize: "16px",
                  width: "100%",
                }}
              />
            ) : (
              <input
                value={value}
                onChange={(e) => onChange(e.currentTarget.value)}
                className="custom-input"
              />
            )}
          </Box>
        </Flex>
      </Box>
      {error && error.length > 0 && (
        <Text color="#B00020" size="xs">
          {error[0]}
        </Text>
      )}
    </>
  );
};

export interface Contact {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  twitter: string;
  facebook: string;
  linkedIn: string;
  github: string;
  city: string;
  country: string;
  self_description: string;
}

interface Props {
  setChanges: (t: boolean) => void;
}

export const EditContactInfo: React.FC<Props> = ({ setChanges }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<Partial<Contact>>({});
  const [image, setImage] = useState<File | null>(null);
  const [scale, setScale] = useState<number>(1);
  const editorRef = useRef<AvatarEditor | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { profile, fetchProfile, setUserMarketChanges } = useProfileStore();
  const { access_token } = useAuthStore();
  const mobile = useMediaQuery("(max-width: 820px)");

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [initialValues, setInitialValues] = useState<Partial<Contact>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  useEffect(() => {
    const foo = async () => {
      const res = await fetchPersonalInfo();

      const initialProfile = {
        first_name: res.first_name,
        last_name: res.last_name,
        self_description: res.self_description,
        ...res.contact_details,
      };
      setInitialValues(initialProfile);
      setValues(initialProfile);
    };
    foo();
  }, [profile]);

  const hasChanges = (): boolean => {
    return !isEqual(values, initialValues);
  };

  const handleChange = (field: keyof Contact) => (value: string) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    setFieldErrors((prev) => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const handleSubmit = async () => {
    setFieldErrors({});

    const newErrors: Record<string, string[]> = {};

    if (!values.first_name?.trim()) {
      newErrors.first_name = [t("field_required")];
    }
    if (!values.last_name?.trim()) {
      newErrors.last_name = [t("field_required")];
    }

    if (!values.self_description?.trim()) {
      delete values.self_description;
    }

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      return; // не відправляємо запит
    }

    const hasCityChanged = values.city !== initialValues.city;
    const hasCountryChanged = values.country !== initialValues.country;

    if (hasCityChanged || hasCountryChanged) {
      setUserMarketChanges(true);
    }

    try {
      setIsSubmitting(true); // Блокуємо кнопку
      const {
        first_name,
        last_name,
        email,
        phone,
        twitter,
        facebook,
        linkedIn,
        github,
        city,
        country,
        self_description,
      } = values;

      const payload: DeepPartial<PersonalData> = {
        first_name,
        last_name,
        self_description,
        contact_details: {
          email,
          phone,
          twitter,
          facebook,
          linkedIn,
          github,
          city,
          country,
        },
      };

      const res = await updatePersonalInfo(payload);

      setInitialValues({
        first_name: res.first_name,
        last_name: res.last_name,
        self_description: res.self_description,
        ...res.contact_details,
      });
      setChanges(true);
      // fetchProfile();

      notifications.show({
        title: t("success"),
        message: t("changed_successfully"),
        icon: <EditIconSmall />,
        color: "#CEB7FF",
        style: {
          border: "1px solid #d3d3d3",
          borderRadius: "8px",
        },
      });
    } catch (error: any) {
      setFieldErrors(error.errors.contact_details);
    }
    setIsSubmitting(false);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarSave = () => {
    const editor = editorRef.current;
    if (editor && image && access_token) {
      const canvas = editor.getImageScaledToCanvas();
      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], image.name, { type: "image/jpeg" });
          try {
            const response = await uploadAvatar({
              image: file,
              token: access_token,
            });
            if (response.status === "success") {
              await fetchProfile();
              setImage(null);
            }
          } catch (error) {
            console.error("Upload error:", error);
          }
        }
      }, "image/jpeg");
    }
  };

  const url = process.env.REACT_APP_BACKEND_URL;
  const cleanedUrl = url?.endsWith("/") ? url.slice(0, -1) : url;

  const avatarFile = profile?.avatar?.file;

  const avatarUrl = avatarFile?.startsWith("http")
    ? avatarFile
    : `${cleanedUrl}${avatarFile ?? ""}`;

  return (
    <Box>
      <Box px="sm">
        <Stack gap="md">
          <Flex gap={30} justify="start" align="center" mb="md" wrap="wrap">
            {image ? (
              <Flex
                gap={30}
                align="flex-end"
                justify="center"
                wrap="wrap"
                style={{ width: "100%", maxWidth: "100%" }}
              >
                <Box
                  style={
                    mobile
                      ? {}
                      : {
                          maxWidth: 300,
                          width: "100%",
                          flex: "1 1 300px",
                        }
                  }
                >
                  <AvatarEditor
                    ref={editorRef}
                    image={URL.createObjectURL(image)}
                    width={150}
                    height={150}
                    border={40}
                    borderRadius={150}
                    scale={scale}
                    rotate={0}
                  />
                  <Box mt="md">
                    <Text size="sm" mb={8}>
                      {t("adjust_scale")}
                    </Text>
                    <Slider
                      value={scale}
                      onChange={setScale}
                      min={1}
                      max={2}
                      step={0.01}
                      label={(value) => value.toFixed(2)}
                    />
                  </Box>
                </Box>

                <Box
                  mb={20}
                  style={{
                    maxWidth: 400,
                    width: "100%",
                    flex: "1 1 300px",
                  }}
                >
                  <Stack gap="sm">
                    <Text size="md" mb={10}>
                      {t("format_image")}
                    </Text>
                    <Flex gap={10} wrap="wrap">
                      <Button onClick={handleAvatarSave} fullWidth>
                        {t("upload_button")}
                      </Button>
                      <Button
                        variant="default"
                        onClick={() => setImage(null)}
                        fullWidth
                      >
                        {t("cancel")}
                      </Button>
                    </Flex>
                  </Stack>
                </Box>
              </Flex>
            ) : (
              <>
                <Avatar
                  src={avatarUrl}
                  radius="xl"
                  size={150}
                  style={{ width: 150, height: 150, borderRadius: "50%" }}
                />
                <Box>
                  <Text size="sm" mb={8}>
                    {t("supported_formats")} JPEG, JPG, PNG
                  </Text>
                  <Text size="sm" mb={12}>
                    {t("max_size")}: 5MB
                  </Text>
                  <Button onClick={handleClickUpload}>
                    {t("choose_image")}
                  </Button>
                  {/* <Button
                    variant="outline"
                    color="rgba(111, 64, 226, 0.5)"
                    ml={20}
                    style={{ padding: 10 }}
                  >
                    <TrashCan />
                  </Button> */}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif"
                    style={{ display: "none" }}
                    ref={fileInputRef}
                    onChange={handleImageChange}
                  />
                </Box>
              </>
            )}
          </Flex>

          {/* Інші поля профілю */}
          <Flex gap="md" wrap="wrap" justify="center">
            <Box w={{ base: "100%", sm: "calc(50% - 0.5rem)" }}>
              {renderField(
                t("first_name"),
                values.first_name || "",
                handleChange("first_name"),
                "name",
                fieldErrors["first_name"]
              )}
            </Box>
            <Box w={{ base: "100%", sm: "calc(50% - 0.5rem)" }}>
              {renderField(
                t("last_name"),
                values.last_name || "",
                handleChange("last_name"),
                "name",
                fieldErrors["last_name"]
              )}
            </Box>

            <Box w={{ base: "100%", sm: "calc(50% - 0.5rem)" }}>
              {renderField(
                t("city"),
                values.city || "",
                handleChange("city"),
                "city"
              )}
            </Box>
            <Box w={{ base: "100%", sm: "calc(50% - 0.5rem)" }}>
              {renderField(
                t("country"),
                values.country || "",
                handleChange("country"),
                "location"
              )}
            </Box>

            <Box w={{ base: "100%", sm: "calc(50% - 0.5rem)" }}>
              {renderField(
                t("email"),
                values.email || "",
                handleChange("email"),
                "email",
                fieldErrors["email"]
              )}
            </Box>
            <Box w={{ base: "100%", sm: "calc(50% - 0.5rem)" }}>
              {renderField(
                t("phone"),
                values.phone || "",
                handleChange("phone"),
                "phone"
              )}
            </Box>

            <Box w={{ base: "100%", sm: "calc(50% - 0.5rem)" }}>
              {renderField(
                t("linkedIn"),
                values.linkedIn || "",
                handleChange("linkedIn"),
                "linkedIn",
                fieldErrors["linkedIn"]
              )}
            </Box>
            <Box w={{ base: "100%", sm: "calc(50% - 0.5rem)" }}>
              {renderField(
                t("twitter"),
                values.twitter || "",
                handleChange("twitter"),
                "twitter",
                fieldErrors["twitter"]
              )}
            </Box>

            <Box w={{ base: "100%", sm: "calc(50% - 0.5rem)" }}>
              {renderField(
                t("facebook"),
                values.facebook || "",
                handleChange("facebook"),
                "facebook",
                fieldErrors["facebook"]
              )}
            </Box>
            <Box w={{ base: "100%", sm: "calc(50% - 0.5rem)" }}>
              {renderField(
                t("github"),
                values.github || "",
                handleChange("github"),
                "github",
                fieldErrors["github"]
              )}
            </Box>
            <Box w={{ base: "100%", sm: "100%" }}>
              {renderField(
                t("self_description"),
                values.self_description || "",
                handleChange("self_description"),
                "self_description"
              )}
            </Box>
          </Flex>

          <Flex justify="flex-end">
            <Button
              mt="md"
              onClick={handleSubmit}
              disabled={!hasChanges() || isSubmitting}
            >
              {t("save")}
            </Button>
          </Flex>
        </Stack>
      </Box>
    </Box>
  );
};
